import type * as api from "./api";
import { fillOf, flagsOf, inRotation, loadGal, type PickupRow, STATUS_TEXT as ACCOUNT_STATUS_TEXT, netGal } from "./accounts";
import { earlyPullOf } from "./calendar";
import { STOPS_PER_HOUR } from "./cycle";
import { sourceKey } from "./dataSource";
import { stopLoad } from "./driverRoutes";
import { REASON_TEXT as CALL_REASON_TEXT } from "./calls";
import { REASON_TEXT as ISSUE_REASON_TEXT } from "./issues";
import { MISSING_TEXT } from "./followUps";
import { STATUS_TEXT as DEAL_STATUS_TEXT } from "./deals";
import { pointText } from "./fleet";
import { SOURCE_TEXT, STAGES } from "./leads";
import { loadState } from "./loads";
import { oldEntryWhat, RESULTS, REVIEW_KINDS } from "./review";
import type { RoleRow } from "./roles";
import { ACCURACY_HELP, accuracyValue } from "./scorecard";
import type { SortColumn } from "./sort";
import { STATUS_TEXT as UNIT_STATUS_TEXT, whereText } from "./units";
import { KIND_TEXT as ORDER_KIND_TEXT } from "./workOrders";

const ACCOUNT_STATUSES = Object.keys(ACCOUNT_STATUS_TEXT);

/** The header help of the account, cycle board and route columns (rule U-28). */
export const HEAD_HELP = {
  flags: "Warnings from the estimate: near full, small container, rate high or low, high scrap, seasonal and early pull. Hover a flag for its meaning. With the keyboard, the focus on the account number shows the help of the row.",
  area: "The service area of the account.",
  areaWeeks: "The service area of the account, and the week group of the rotation.",
  status: "The service status: New, Regular, On call, On hold or Ended. Hover a status for its meaning. With the keyboard, the focus on the account number shows the help of the row.",
  source: "Who owns the fields of the account: the source (for example the sheet) or the app.",
  accountLoad: "The approximate gallons in the containers now, from the fill estimate (the level dot on the maps). With no estimate yet, from the stated rate: the hover of the cell then says \"From the stated rate\". It is not a measurement.",
  boardLoad: "The estimated gallons in the containers on the first day of the week (today for this week), from the fill estimate. With no estimate yet, from the stated rate. It is not a measurement.",
  boardShare: "The fill estimate on the first day of the week (today for this week) as a share of the container size. With no estimate yet, from the stated rate.",
  recommended: "The recommended container size for one cycle and the margin.",
  accountShare: "The approximate fill as a share of the container size, the same value as the level dot. With no estimate yet, from the stated rate: the hover of the cell then says \"From the stated rate\".",
  left: "The weeks until the containers are full at the estimated rate.",
  visit: "The next visit of the rotation, and the estimated fill at that visit.",
  pickup: "The day of the last pickup.",
  board: "The stops that go on the routes of the week: the default selection and the choices of the dispatcher.",
  stops: "The stops on the board of the week in this area. ('1 of 2' means 1 stop scheduled this week out of 2 accounts)",
  gal: "The estimated gallons of these stops.",
  days: `Rough estimate of how much truck time the selected stops need (simple ${STOPS_PER_HOUR} stops per hour)`,
  order: "The place of the stop on the route.",
  arrive: "The planned arrival time.",
  window: "The time window of the account for a visit.",
  stopLoad: "The collected gallons of a done stop, else the estimated gallons.",
  state: "Planned, done or skipped.",
} as const;

export const TANK_COLUMNS: SortColumn<api.Tank>[] = [
  { key: "tank", label: "Container", value: (t) => t.label },
  { key: "level", label: "Level", value: (t) => t.state?.measurement.level_state, order: ["full", "not_full"] },
  { key: "battery", label: "Battery", value: (t) => t.state?.quality.battery_pct, num: true },
  { key: "reading", label: "Last reading", value: (t) => t.state?.observed_at, first: "desc" },
];

export const ACCOUNT_COLUMNS: SortColumn<api.Account>[] = [
  { key: "account", label: "Account", value: (a) => a.number, width: 7.5 },
  { key: "name", label: "Name", value: (a) => a.name, width: 9.5 },
  { key: "flags", label: "Flags", value: (a) => flagsOf(a).length + (earlyPullOf(a) ? 1 : 0) || null, first: "desc", help: HEAD_HELP.flags, width: 9 },
  { key: "area", label: "Area", value: (a) => a.plan?.area, help: HEAD_HELP.areaWeeks, width: 6 },
  { key: "status", label: "Status", value: (a) => a.status, order: ACCOUNT_STATUSES, help: HEAD_HELP.status, width: 5.5 },
  { key: "source", label: "Source", value: (a) => sourceKey(a.data_source) || null, help: HEAD_HELP.source, width: 6.5 },
  { key: "load", label: "Estimated load", value: (a) => loadGal(a), first: "desc", num: true, help: HEAD_HELP.accountLoad, width: 7 },
  {
    key: "recommended",
    label: "Recommended",
    value: (a) => (a.status === "OFF" ? null : a.projection?.recommended_gal),
    first: "desc",
    off: true,
    num: true,
    help: HEAD_HELP.recommended,
    width: 10,
  },
  { key: "share", label: "Full", value: (a) => fillOf(a)?.share, first: "desc", num: true, help: HEAD_HELP.accountShare, width: 4.5 },
  { key: "left", label: "Weeks left", value: (a) => (inRotation(a) ? a.projection?.weeks_left : null), off: true, num: true, help: HEAD_HELP.left, width: 5.5 },
  { key: "visit", label: "Next visit", value: (a) => (inRotation(a) ? a.projection?.next_visit : null), help: HEAD_HELP.visit, width: 6.5 },
  { key: "pickup", label: "Last pickup", value: (a) => a.last_pickup, first: "desc", off: true, help: HEAD_HELP.pickup, width: 6.5 },
];

const ISSUE_STATUSES = ["assigned", "acknowledged", "resolved", "closed"];

export const ISSUE_COLUMNS: SortColumn<api.Issue>[] = [
  { key: "reason", label: "Reason", value: (i) => ISSUE_REASON_TEXT[i.reason] },
  { key: "state", label: "State", value: (i) => i.status, order: ISSUE_STATUSES },
  { key: "account", label: "Account", value: (i) => i.account_number },
  { key: "assignee", label: "Assignee", value: (i) => i.assignee_email },
  { key: "opened", label: "Opened", value: (i) => i.opened_at, first: "desc" },
  { key: "due", label: "Due", value: (i) => i.due_date },
];

export const ORDER_COLUMNS: SortColumn<api.WorkOrder>[] = [
  { key: "kind", label: "Work", value: (o) => ORDER_KIND_TEXT[o.kind] },
  { key: "state", label: "State", value: (o) => o.status, order: ["open", "done", "cancelled"] },
  { key: "account", label: "Account", value: (o) => o.account_number },
  { key: "drop", label: "Drop", value: (o) => o.to_size, first: "desc", num: true },
  { key: "pull", label: "Pull", value: (o) => o.from_size, first: "desc", num: true },
  { key: "opened", label: "Opened", value: (o) => o.opened_at, first: "desc" },
  { key: "by", label: "Opened by", value: (o) => o.opened_by },
];

export const CALL_COLUMNS: SortColumn<api.Call>[] = [
  { key: "at", label: "When", value: (c) => c.at, first: "desc" },
  { key: "reason", label: "Reason", value: (c) => CALL_REASON_TEXT[c.reason] },
  { key: "caller", label: "Caller", value: (c) => c.caller },
  { key: "fault", label: "At fault", value: (c) => (c.at_fault ? 1 : 0), first: "desc" },
];

export const PICKUP_COLUMNS: SortColumn<PickupRow>[] = [
  { key: "at", label: "Date", value: (r) => r.pickup.at, first: "desc" },
  { key: "raw", label: "Raw", value: (r) => r.pickup.raw_gal, first: "desc", num: true },
  { key: "scrap", label: "Scrap", value: (r) => r.pickup.scrap_gal, first: "desc", num: true },
  { key: "net", label: "Net", value: (r) => netGal(r.pickup), first: "desc", num: true },
];

export const REVIEW_COLUMNS: SortColumn<api.ReviewItem>[] = [
  { key: "account", label: "Account", value: (i) => i.number },
  { key: "reason", label: "Reason", value: (i) => i.gap, first: "desc" },
  { key: "rate", label: "Rate", value: (i) => i.measured_rate, first: "desc", num: true },
  { key: "capacity", label: "Containers", value: (i) => i.capacity_gal, first: "desc" },
  { key: "recommended", label: "Recommended", value: (i) => i.recommended_gal, first: "desc" },
];

/** The "Saved away from the stop" table of the Review page. */
export const PLACE_ENTRY_COLUMNS: SortColumn<api.PlaceEntry>[] = [
  { key: "account", label: "Account", value: (e) => e.number },
  { key: "visit", label: "Visit", value: (e) => e.at, first: "desc" },
  { key: "entry", label: "Entry", value: (e) => oldEntryWhat(e) },
  { key: "driver", label: "Driver", value: (e) => e.driver_name },
  { key: "place", label: "Place", value: (e) => e.place.distance_m ?? null, first: "desc" },
];

export const REVIEW_RECORD_COLUMNS: SortColumn<api.ReviewRecord>[] = [
  { key: "at", label: "Date", value: (r) => r.decided_at, first: "desc" },
  { key: "kind", label: "Kind", value: (r) => r.kind, order: [...REVIEW_KINDS] },
  { key: "result", label: "Result", value: (r) => r.result, order: [...RESULTS] },
  { key: "reason", label: "Reason", value: (r) => r.note },
  { key: "by", label: "By", value: (r) => r.decided_by },
  { key: "until", label: "Until", value: (r) => r.until, first: "desc" },
];

export const LOAD_COLUMNS: SortColumn<api.Load>[] = [
  { key: "code", label: "Load", value: (l) => l.code, first: "desc" },
  { key: "truck", label: "Truck", value: (l) => l.truck },
  { key: "driver", label: "Driver", value: (l) => (l.mine ? "" : l.driver?.toLowerCase()) },
  { key: "state", label: "State", value: (l) => loadState(l), order: ["open", "measured", "approved"] },
  { key: "stops", label: "Pickups", value: (l) => l.stops.length + l.hidden_stops, first: "desc", num: true },
  { key: "raw", label: "Raw", value: (l) => l.raw_gal, first: "desc", num: true },
  { key: "scrap", label: "Scrap", value: (l) => l.scrap_gal, first: "desc", num: true },
  { key: "result", label: "Processor result", value: (l) => l.result?.scrap_pct, first: "desc", num: true },
];

export const STOP_COLUMNS: SortColumn<api.CycleStop>[] = [
  { key: "visit", label: "Visit", value: (s) => (s.selected ? 0 : 1), help: HEAD_HELP.board },
  { key: "account", label: "Account", value: (s) => s.number },
  { key: "area", label: "Area", value: (s) => s.area, help: HEAD_HELP.area },
  { key: "status", label: "Status", value: (s) => s.status, order: ACCOUNT_STATUSES, help: HEAD_HELP.status },
  { key: "load", label: "Estimated load", value: (s) => s.estimated_gal, first: "desc", num: true, help: HEAD_HELP.boardLoad },
  { key: "share", label: "Full", value: (s) => s.load_share, first: "desc", num: true, help: HEAD_HELP.boardShare },
  { key: "left", label: "Weeks left", value: (s) => s.weeks_left, num: true, help: HEAD_HELP.left },
  { key: "flags", label: "Flags", value: (s) => s.flags.length + (s.early_pull ? 1 : 0) || null, first: "desc", help: HEAD_HELP.flags },
];

export type AreaRow = api.CycleBoard["areas"][number];

export const AREA_COLUMNS: SortColumn<AreaRow>[] = [
  { key: "area", label: "Area", value: (a) => a.area },
  { key: "stops", label: "Stops", value: (a) => a.capacity.stops, first: "desc", num: true, help: HEAD_HELP.stops },
  { key: "gal", label: "Estimated gal", value: (a) => a.capacity.gallons, first: "desc", num: true, help: HEAD_HELP.gal },
  { key: "days", label: "Truck-days of 8 h", value: (a) => a.capacity.truck_days, first: "desc", num: true, help: HEAD_HELP.days },
];

export const WEEK_COLUMNS: SortColumn<api.ScorecardWeek>[] = [
  { key: "week", label: "Week", value: (w) => w.week_start, first: "desc" },
  { key: "fault", label: "Calls at fault", value: (w) => w.calls_at_fault, first: "desc", num: true },
  { key: "overflows", label: "Overflows / 100", value: (w) => w.overflows_per_100, first: "desc", num: true },
  { key: "accuracy", label: "Accuracy", value: accuracyValue, num: true, help: ACCURACY_HELP },
  { key: "done", label: "Stops done", value: (w) => (w.planned_stops ? w.done_stops / w.planned_stops : null), num: true },
  { key: "extra", label: "Extra stops", value: (w) => w.extra_stops, first: "desc", num: true },
];

const SALES_NUMBERS: SortColumn<api.SalesRow>[] = [
  { key: "wins", label: "New accounts", value: (r) => r.wins, first: "desc", num: true },
  { key: "renewals", label: "Renewals", value: (r) => r.renewals, first: "desc", num: true },
  { key: "accounts", label: "All accounts", value: (r) => r.accounts, first: "desc", num: true },
  { key: "active", label: "Commercial active %", value: (r) => r.active, first: "desc", num: true },
  { key: "regular", label: "Regular %", value: (r) => r.regular, first: "desc", num: true },
  { key: "on_call", label: "On call %", value: (r) => r.on_call, first: "desc", num: true },
  { key: "signed", label: "Signed regular %", value: (r) => r.signed_regular, first: "desc", num: true },
];

export const SALES_PEOPLE_COLUMNS: SortColumn<api.SalesRow>[] = [
  { key: "person", label: "Salesperson", value: (r) => (r.key ? r.name || "~" : "~~") },
  SALES_NUMBERS[0],
  SALES_NUMBERS[1],
  { key: "losses", label: "Lost leads", value: (r) => r.losses, first: "desc", num: true },
  { key: "hours", label: "Hours per sign-up", value: (r) => r.hours_per_win, num: true },
  ...SALES_NUMBERS.slice(2, 4),
  ...SALES_NUMBERS.slice(4).map((column) => ({ ...column, off: true })),
];

export const SALES_STATE_COLUMNS: SortColumn<api.SalesRow>[] = [
  { key: "state", label: "State", value: (r) => r.key || "~" },
  ...SALES_NUMBERS,
];

export const SALES_WEEK_COLUMNS: SortColumn<api.SalesWeek>[] = [
  { key: "week", label: "Week", value: (w) => w.week_start, first: "desc" },
  { key: "wins", label: "New accounts", value: (w) => w.wins, first: "desc", num: true },
  { key: "renewals", label: "Renewals", value: (w) => w.renewals, first: "desc", num: true },
  { key: "losses", label: "Lost leads", value: (w) => w.losses, first: "desc", num: true },
];

export const columnOf = <T>(columns: readonly SortColumn<T>[], key: string): SortColumn<T> => {
  const found = columns.find((c) => c.key === key);
  if (!found) throw new Error(`No column ${key}`);
  return found;
};

export const LEAD_COLUMNS: SortColumn<api.Lead>[] = [
  { key: "name", label: "Lead", value: (l) => l.name },
  { key: "stage", label: "Stage", value: (l) => l.stage, order: [...STAGES] },
  { key: "owner", label: "Owner", value: (l) => l.owner_email },
  { key: "address", label: "Address", value: (l) => l.address || null },
  { key: "source", label: "Source", value: (l) => SOURCE_TEXT[l.source], off: true },
  { key: "changed", label: "Changed", value: (l) => l.updated_at, first: "desc" },
];

export const FOLLOW_UP_COLUMNS: SortColumn<api.FollowUp>[] = [
  { key: "lead", label: "Lead", value: (f) => f.lead_name },
  { key: "due", label: "Due", value: (f) => `${f.due_on} ${f.due_at ?? "99:99"}` },
  { key: "note", label: "To do", value: (f) => f.note },
  { key: "missing", label: "Missing data", value: (f) => f.missing.map((m) => MISSING_TEXT[m]).join(", ") || null },
  { key: "stage", label: "Stage", value: (f) => f.stage, order: [...STAGES] },
];

export const IMPORT_COLUMNS: SortColumn<api.ImportRow>[] = [
  { key: "line", label: "Line", value: (r) => r.line },
  { key: "name", label: "Name", value: (r) => r.fields.name || null },
  { key: "address", label: "Address", value: (r) => r.fields.address || null },
  { key: "owner", label: "Owner", value: (r) => r.fields.owner || null },
  { key: "check", label: "Check", value: (r) => (r.errors.length ? 0 : r.matches.length || r.repeats.length ? 1 : 2) },
];

const DEAL_STATUSES = Object.keys(DEAL_STATUS_TEXT);

export const DEAL_COLUMNS: SortColumn<api.Deal>[] = [
  { key: "deal", label: "Deal", value: (d) => d.site_name || d.lead_name },
  { key: "status", label: "Status", value: (d) => d.status, order: DEAL_STATUSES },
  { key: "owner", label: "Salesperson", value: (d) => d.owner_email },
  { key: "state", label: "State", value: (d) => d.state || null },
  { key: "rebate", label: "Rebate", value: (d) => d.rebate_value },
  { key: "approval", label: "Outside limits", value: (d) => d.reasons_now.length || null, first: "desc" },
  { key: "changed", label: "Changed", value: (d) => d.updated_at, first: "desc" },
];

export const RENEWAL_COLUMNS: SortColumn<api.Renewal>[] = [
  { key: "account", label: "Account", value: (r) => r.number },
  { key: "name", label: "Name", value: (r) => r.name },
  { key: "ends", label: "Ends", value: (r) => r.ends_on },
  { key: "left", label: "Days left", value: (r) => r.days_left, num: true },
  { key: "renews", label: "Renews", value: (r) => (r.auto_renew ? 1 : 0), first: "desc", off: true },
  { key: "notice", label: "Notice by", value: (r) => r.notice_by },
  { key: "salesperson", label: "Salesperson", value: (r) => r.salesperson },
  { key: "next", label: "Next check-in", value: (r) => r.next_check_in },
  { key: "checked", label: "Last check-in", value: (r) => r.last_check_in, first: "desc", off: true },
  { key: "version", label: "Version", value: (r) => r.version, off: true },
];

export const CONTRACT_VERSION_COLUMNS: SortColumn<api.Contract>[] = [
  { key: "version", label: "Version", value: (c) => c.version, first: "desc" },
  { key: "starts", label: "Starts", value: (c) => c.starts_on, first: "desc" },
  { key: "ends", label: "Ends", value: (c) => c.ends_on, first: "desc" },
  { key: "term", label: "Term", value: (c) => c.term_months },
  { key: "rebate", label: "Rebate", value: (c) => c.rebate_value },
  { key: "renews", label: "Renews", value: (c) => (c.auto_renew ? c.notice_days : null) },
  { key: "consideration", label: "Added consideration", value: (c) => c.consideration_amount || c.consideration || null, off: true },
  { key: "payee", label: "Payee", value: (c) => c.payee_name },
  { key: "recorded", label: "Recorded", value: (c) => c.recorded_at, first: "desc", off: true },
];

export const CHECK_IN_COLUMNS: SortColumn<api.CheckIn>[] = [
  { key: "day", label: "Day", value: (k) => k.met_on, first: "desc" },
  { key: "rating", label: "Rating", value: (k) => k.rating, first: "desc" },
  { key: "contact", label: "Contact", value: (k) => k.contact || null },
  { key: "result", label: "Result", value: (k) => k.result || null },
  { key: "next", label: "Next check-in", value: (k) => k.next_on },
  { key: "by", label: "By", value: (k) => k.recorded_email },
];

export const DOCUMENT_COLUMNS: SortColumn<api.ContractDocument>[] = [
  { key: "file", label: "File", value: (d) => d.file_name },
  { key: "kind", label: "Kind", value: (d) => d.kind },
  { key: "size", label: "Size", value: (d) => d.size_bytes, off: true, num: true },
  { key: "stored", label: "Added", value: (d) => d.stored_at, first: "desc" },
  { key: "by", label: "By", value: (d) => d.uploaded_email },
];

const RUN_STATUSES = ["draft", "approved", "exported", "paid"];
const PAYMENTS = ["held", "failed", "due", "paid"];

export const RUN_COLUMNS: SortColumn<api.PayoutRun>[] = [
  { key: "run", label: "Run", value: (r) => r.tag },
  { key: "period", label: "Period", value: (r) => r.ends_on, first: "desc" },
  { key: "status", label: "Status", value: (r) => r.status, order: RUN_STATUSES },
  { key: "lines", label: "Lines", value: (r) => r.lines, first: "desc", off: true, num: true },
  { key: "held", label: "Held", value: (r) => r.held || null, first: "desc", num: true },
  { key: "total", label: "To pay", value: (r) => r.total, first: "desc", num: true },
  { key: "made", label: "Made by", value: (r) => r.created_email, off: true },
  { key: "approved", label: "Approved by", value: (r) => r.approved_email },
];

export const PAYOUT_LINE_COLUMNS: SortColumn<api.PayoutLine>[] = [
  { key: "account", label: "Account", value: (l) => l.number },
  { key: "payee", label: "Payee", value: (l) => l.payee_name || null },
  { key: "net", label: "Net gal", value: (l) => l.net_gal, first: "desc", num: true },
  { key: "rate", label: "Rebate", value: (l) => l.rate, num: true },
  { key: "amount", label: "Amount", value: (l) => l.amount, first: "desc", off: true, num: true },
  { key: "adjustment", label: "Adjustment", value: (l) => l.adjustment || null, first: "desc", off: true, num: true },
  { key: "final", label: "To pay", value: (l) => l.final_amount, first: "desc", num: true },
  { key: "payment", label: "Payment", value: (l) => l.payment_status, order: PAYMENTS },
  { key: "hold", label: "Hold reason", value: (l) => l.hold_reason || null },
];

export const ACCOUNT_PAYOUT_COLUMNS: SortColumn<api.AccountPayout>[] = [
  { key: "run", label: "Run", value: (p) => p.tag },
  { key: "period", label: "Period", value: (p) => p.ends_on, first: "desc" },
  { key: "net", label: "Net gal", value: (p) => p.net_gal, first: "desc", num: true },
  { key: "final", label: "Amount", value: (p) => p.final_amount, first: "desc", num: true },
  { key: "payment", label: "Payment", value: (p) => p.payment_status, order: PAYMENTS },
  { key: "paid", label: "Paid on", value: (p) => p.paid_on, first: "desc" },
];

export const ROUTE_STOP_COLUMNS: SortColumn<api.RouteStop>[] = [
  { key: "order", label: "Order", value: (s) => s.position, num: true, help: HEAD_HELP.order },
  { key: "arrive", label: "Arrive", value: (s) => s.arrive ?? null, help: HEAD_HELP.arrive },
  { key: "account", label: "Account", value: (s) => s.number },
  { key: "area", label: "Area", value: (s) => s.area, help: HEAD_HELP.area },
  { key: "window", label: "Window", value: (s) => s.window[0], help: HEAD_HELP.window },
  { key: "load", label: "Load", value: (s) => stopLoad(s), first: "desc", num: true, help: HEAD_HELP.stopLoad },
  { key: "state", label: "State", value: (s) => s.state, order: ["planned", "done", "skipped"], help: HEAD_HELP.state },
];

export const UNROUTED_COLUMNS: SortColumn<api.CycleStop>[] = [
  { key: "account", label: "Account", value: (s) => s.number },
  { key: "area", label: "Area", value: (s) => s.area, help: HEAD_HELP.area },
  { key: "window", label: "Window", value: (s) => s.window[0], help: HEAD_HELP.window },
  { key: "load", label: "Estimated load", value: (s) => s.estimated_gal, first: "desc", num: true, help: HEAD_HELP.boardLoad },
  { key: "flags", label: "Flags", value: (s) => s.flags.length || null, first: "desc", help: HEAD_HELP.flags },
];

export const USER_COLUMNS: SortColumn<api.Member>[] = [
  { key: "user", label: "Name", value: (m) => (m.name || m.email).toLowerCase() },
  { key: "email", label: "Email", value: (m) => m.email, off: true },
  { key: "roles", label: "Roles", value: (m) => m.roles.map((r) => r.role).join(",") || null },
  { key: "signin", label: "Last sign-in", value: (m) => m.last_sign_in, first: "desc" },
  { key: "status", label: "Status", value: (m) => m.status, order: ["active", "deactivated"] },
  { key: "truck", label: "Preferred truck", value: (m) => m.preferred_truck },
  { key: "added", label: "Added", value: (m) => m.created_at, first: "desc", off: true },
];

export const ROLE_COLUMNS: SortColumn<RoleRow>[] = [
  { key: "role", label: "Role", value: (r) => r.name.toLowerCase() },
  { key: "kind", label: "Kind", value: (r) => r.kind, order: ["built-in", "custom"] },
  { key: "base", label: "Based on", value: (r) => r.base },
  { key: "modules", label: "Modules", value: (r) => r.modules, first: "desc", num: true },
  { key: "members", label: "Members", value: (r) => r.assigned, first: "desc", num: true },
  { key: "added", label: "Added", value: (r) => r.created_at, first: "desc", off: true },
];

export const KEY_COLUMNS: SortColumn<api.ApiKey>[] = [
  { key: "key", label: "Name", value: (k) => k.label.toLowerCase() },
  { key: "kid", label: "Key ID", value: (k) => k.kid, off: true },
  { key: "hubs", label: "Hubs", value: (k) => k.hub_ids.length || null },
  { key: "status", label: "Status", value: (k) => k.status, order: ["active", "expired", "revoked"] },
  { key: "made", label: "Made", value: (k) => k.created_at, first: "desc" },
  { key: "used", label: "Last used", value: (k) => k.last_used_at, first: "desc" },
  { key: "ends", label: "Ends", value: (k) => k.expires_at },
];

export const UNIT_COLUMNS: SortColumn<api.Unit>[] = [
  { key: "serial", label: "Serial", value: (u) => u.serial.toLowerCase() },
  { key: "size", label: "Size", value: (u) => u.size_gal, num: true },
  { key: "status", label: "Status", value: (u) => u.status, order: Object.keys(UNIT_STATUS_TEXT) },
  { key: "where", label: "Where", value: (u) => whereText(u).toLowerCase() },
  { key: "moved", label: "Last move", value: (u) => u.last_move_at ?? null, first: "desc" },
  { key: "note", label: "Note", value: (u) => u.note.toLowerCase() || null, off: true },
];

export const DEPOT_COLUMNS: SortColumn<api.Depot>[] = [
  { key: "name", label: "Depot", value: (d) => d.name.toLowerCase() },
  { key: "address", label: "Address", value: (d) => d.address.toLowerCase() || null },
  { key: "point", label: "Point", value: (d) => (d.lat === null ? null : pointText(d)) },
  { key: "opens", label: "Opens", value: (d) => d.opens },
  { key: "closes", label: "Closes", value: (d) => d.closes },
];

export const TRUCK_COLUMNS: SortColumn<api.Truck>[] = [
  { key: "code", label: "Truck", value: (t) => t.code },
  { key: "depot", label: "Depot", value: (t) => t.hub_name.toLowerCase() },
  { key: "capacity", label: "Capacity", value: (t) => t.capacity_gal, first: "desc", num: true },
  { key: "shift", label: "Shift", value: (t) => `${t.shift_start}-${t.shift_end}` },
  { key: "status", label: "Status", value: (t) => (t.active ? "active" : "inactive"), order: ["active", "inactive"] },
  { key: "home", label: "Home areas", value: (t) => t.home_areas.map((area) => area.code).join(", ") || null },
  { key: "routes", label: "Routes", value: (t) => t.routes, first: "desc", off: true, num: true },
];
